/**
 * Speech ↔ camera contract for voice-driven parameter changes.
 *
 * Timeline is planned before the tool returns: the viewer reports the exact
 * camera move durations, narration is written to fill that window, and the
 * camera move starts on the first audible TTS frame so both end together.
 */

/** Pace this module is balanced at; also the value a bad override falls back to. */
const DEFAULT_VOICE_PACE = 0.7;
const VOICE_PACE_STORAGE_KEY = "tsoles.voicePace";

function readVoicePace(): number {
  try {
    const stored = Number(window.localStorage.getItem(VOICE_PACE_STORAGE_KEY));
    if (Number.isFinite(stored) && stored > 0) return Math.min(2, Math.max(0.2, stored));
  } catch {
    // Private-mode or blocked storage: the default pace is the right answer.
  }
  return DEFAULT_VOICE_PACE;
}

export const VOICE_AGENT_SPEAK_START = "voice:agentSpeakStart";
export const VOICE_AGENT_SPEAK_END = "voice:agentSpeakEnd";
export const VOICE_PARAM_STEP_START = "voice:paramStepStart";
export const VOICE_PARAM_STEP_END = "voice:paramStepEnd";

/**
 * The one number to tune how fast a multi-parameter sequence feels. 1 is the
 * pace this file was balanced at; 0.5 runs the same sequence twice as fast.
 *
 * Only the deliberate waits and the camera travel below are derived from it.
 * Silence detection, poll intervals, and the timeouts that wait on the network
 * or on a surface rebuild are deliberately left out: those are what hold speech,
 * camera, and labels together, and shrinking them desynchronises the sequence
 * rather than speeding it up.
 *
 * Overridable at runtime for tuning without a rebuild:
 *   localStorage.setItem("tsoles.voicePace", "0.6"); location.reload();
 */
export const VOICE_PACE = readVoicePace();

/** Scale a pace-sensitive duration. Never rounds down to zero. */
const paced = (baselineMs: number): number => Math.max(1, Math.round(baselineMs * VOICE_PACE));

/** Silence between two parameter steps: camera holds still, assistant says nothing. */
export const VOICE_PARAM_GAP_MS = paced(1000);

/** Budget for the surface rebuild that runs after the camera lands. */
export const VOICE_PARAM_APPLY_MS = 800;

/**
 * Dwell budget used when planning: it stretches the window so the narration is
 * written long enough to sit on the parameter with its label open. The real
 * dwell is then taken from the finished line, not from this number, so speech
 * and camera cannot drift apart. Only a silent step dwells exactly this long.
 */
export const VOICE_PARAM_HOLD_MS = paced(1000);

/**
 * Camera travel, shared with every manual view change so a voice tour and a
 * hand-driven view move at the same speed. Speed rises as the bounds shrink, so
 * a move takes exactly VOICE_PACE of the time it took at pace 1.
 */
export const VOICE_CAMERA_SPEED_UNITS_PER_SEC = 160 / VOICE_PACE;
export const VOICE_CAMERA_MIN_MS = Math.max(250, paced(550));
export const VOICE_CAMERA_MAX_MS = Math.max(VOICE_CAMERA_MIN_MS, paced(2000));

/**
 * Seed narration pace, in words a second, for sizing a sentence to a camera
 * window before this session has heard the voice speak.
 *
 * It was 2.35 for a long time, which is a reading pace rather than the pace the
 * agent's voice actually keeps, and every caller that sized a silence or a
 * camera move from it was therefore holding still for about a fifth longer than
 * the words lasted. The real figure was already written down elsewhere in the
 * tour — the sandal circle had to be cut to 0.82 of the estimate to end with the
 * sentence, which puts the voice at 2.35 / 0.82 ≈ 2.87 words a second.
 */
export const VOICE_WORDS_PER_SEC = 2.85;

/** The same seed for Chinese, where the unit is the character, not the word. */
export const VOICE_HAN_PER_SEC = 4.2;

/** First step of a tour: TTS can be far behind on a bad connection. */
export const VOICE_SPEAK_START_TIMEOUT_MS = 45000;
/** Later steps: a fresh turn has to be asked for, so allow a full round trip. */
export const VOICE_STEP_SPEAK_START_TIMEOUT_MS = 25000;

/**
 * How long to wait for the assistant to finish the sentence it is on. Generous,
 * because the silence after it is client-driven and therefore certain to come.
 */
export const VOICE_STEP_PAUSE_WAIT_MS = 8000;
/** Quiet stretch that counts as finished rather than a breath mid-sentence. */
export const VOICE_PAUSE_QUIET_MS = 300;

/** How long the tour waits for an announced follow-up step before going home. */
export const VOICE_FOLLOWUP_WAIT_MS = 8000;
/** Grace window for an unannounced next step before the camera goes home. */
export const VOICE_TAIL_WAIT_MS = 700;

const AUDIBLE = 0.055;

/**
 * Where the output analyser separates sound from digital silence.
 *
 * The SDK's level is the whole spectrum averaged — 1024 bins — and speech fills
 * only the bottom of it, so a sentence at normal loudness reads well below
 * AUDIBLE. That is why the level was written off as untrustworthy and the tour
 * came to depend on the predicted chunk timeline alone. It is perfectly
 * trustworthy for the narrower question asked here, because the player writes
 * exact zeroes whenever it has nothing to play: is any sound coming out at all.
 */
const OUTPUT_ACTIVE = 0.006;

/**
 * How far past the predicted end the measured level may hold a line open.
 *
 * The prediction assumes chunks arrive faster than they are played. When the
 * server synthesises at about the speed it speaks they do not, the prediction
 * runs dry while the sentence is still going, and the next line then cuts it —
 * which is how the pressure line kept losing "…prescribed for these." The bound
 * is there so that a level which never settles cannot hold a tour open for ever.
 */
const OUTPUT_OVERRUN_MAX_MS = 3000;

let agentSpeaking = false;
let agentOutputVolume = 0;

/**
 * When the assistant's speech will have finished playing, worked out from the
 * audio the server sends rather than from what the speaker measures.
 *
 * The output level alone could not be trusted on phones. There the SDK plays
 * through a different audio context, and its analyser read below the audible
 * threshold for whole sentences — so the tour never saw the assistant speak: it
 * waited out a 25 second timeout on every line, moved the scene long after the
 * words had ended, and kept the microphone open through speech it thought was
 * silence. Every chunk of speech arrives with its own length, which is the same
 * on every device, and playback is sequential, so the moment the last chunk ends
 * is known exactly.
 */
let agentAudioEndsAt = 0;
let lastInterruptEventId = -1;
/**
 * Bytes of audio per millisecond of speech. The agent streams pcm_16000 —
 * 16 000 samples a second, two bytes each — and the format is agreed while the
 * connection is set up, out of the app's sight. Change this with the agent's
 * agent_output_audio_format.
 */
const AGENT_AUDIO_BYTES_PER_MS = 32;
/** Output latency between a chunk arriving and being heard, and the tail after it. */
const AGENT_AUDIO_TAIL_MS = 150;

function base64ByteLength(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/**
 * Timeline of what the voice actually did, for diagnosing a tour that stalls.
 *
 * Kept on window so it can be read from a connected phone over the debug
 * bridge: on a handset there is no console to watch, and the timings are the
 * only way to tell a detector that never fired from audio that never arrived.
 */
export type VoiceLogEntry = { t: number; e: string; d?: string | number };
const voiceLogEntries: VoiceLogEntry[] = [];

export function voiceLog(e: string, d?: string | number): void {
  voiceLogEntries.push({ t: Date.now(), e, d });
  if (voiceLogEntries.length > 400) voiceLogEntries.shift();
  try {
    (window as unknown as { __tsolesVoiceLog?: VoiceLogEntry[] }).__tsolesVoiceLog = voiceLogEntries;
  } catch {
    /* nothing to expose on */
  }
}

/**
 * The last few chunks counted, so the same one is never added to the timeline
 * twice.
 *
 * The SDK hands every parsed message to onIncomingEvent and then, for an audio
 * message, the very same bytes to onAudio. The app listens on both, because
 * neither has proved to be delivered on every transport — so on the desktop
 * each chunk was counted twice and the predicted end of a turn landed at double
 * its real length. That is what the tour waits on, so after every line it sat in
 * silence for as long again as the assistant had just spoken: about eleven
 * seconds after the welcome. A duplicate arrives in the same dispatch as the
 * original, never chunks later, so remembering a handful is enough.
 */
const recentChunkKeys: string[] = [];
const CHUNK_MEMORY = 8;

/** Cheap identity for a chunk: length plus both ends, without keeping the bytes. */
function chunkKey(base64: string): string {
  return `${base64.length}:${base64.slice(0, 24)}:${base64.slice(-24)}`;
}

/** A chunk of the assistant's speech has arrived and will play after anything queued. */
export function noteAgentAudioChunk(base64: string, eventId?: number): void {
  // Mirrors the SDK, which drops audio belonging to a turn that was interrupted.
  if (typeof eventId === "number" && eventId < lastInterruptEventId) return;
  const key = chunkKey(base64);
  if (recentChunkKeys.includes(key)) return;
  recentChunkKeys.push(key);
  if (recentChunkKeys.length > CHUNK_MEMORY) recentChunkKeys.shift();
  const ms = base64ByteLength(base64) / AGENT_AUDIO_BYTES_PER_MS;
  if (ms <= 0) return;
  const wasIdle = !agentAudioPlaying();
  agentAudioEndsAt = Math.max(Date.now(), agentAudioEndsAt) + ms;
  if (wasIdle) voiceLog("audio:first", Math.round(ms));
  voiceLog("audio:queued", Math.round(agentAudioEndsAt - Date.now()));
}

/** The server cut the assistant off; whatever was queued has been flushed. */
export function noteAgentInterruption(eventId?: number): void {
  if (typeof eventId === "number") lastInterruptEventId = eventId;
  agentAudioEndsAt = 0;
  voiceLog("interrupted");
}

/** A new session, or none: nothing is playing. */
export function resetAgentAudioTimeline(): void {
  agentAudioEndsAt = 0;
  lastInterruptEventId = -1;
  // Nothing queued means nothing to tell a duplicate apart from, and the pair of
  // callbacks that produce one always fire in the same dispatch, so a reset can
  // never land between them.
  recentChunkKeys.length = 0;
}

function agentAudioPlaying(): boolean {
  return agentAudioEndsAt > 0 && Date.now() < agentAudioEndsAt + AGENT_AUDIO_TAIL_MS;
}

export function setAgentSpeaking(next: boolean): void {
  if (next === agentSpeaking) return;
  agentSpeaking = next;
  if (!next) agentOutputVolume = 0;
  document.dispatchEvent(new Event(next ? VOICE_AGENT_SPEAK_START : VOICE_AGENT_SPEAK_END));
}

export function setAgentOutputVolume(volume: number): void {
  agentOutputVolume = Math.max(0, Number.isFinite(volume) ? volume : 0);
}

export function isAgentSpeaking(): boolean {
  return agentSpeaking || agentOutputVolume >= AUDIBLE || agentAudioPlaying();
}

/**
 * Audio-only view of the output stream. isAgentSpeaking stays true for a whole
 * turn, so it cannot see the pause between two sentences the assistant speaks
 * in one breath. Only the output level can.
 *
 * Three sources, because no one of them is enough: a level loud enough to be
 * unambiguous, the predicted end of the audio queued so far, and — once that
 * prediction has run out — any sound at all still coming from the player. The
 * last of those can only extend a line, never shorten one, and only so far.
 */
export function isAgentAudible(): boolean {
  if (agentOutputVolume >= AUDIBLE || agentAudioPlaying()) return true;
  if (agentOutputVolume < OUTPUT_ACTIVE) return false;
  return agentAudioEndsAt > 0 && Date.now() < agentAudioEndsAt + OUTPUT_OVERRUN_MAX_MS;
}

/** Live path quiet (mode + level), ignoring predicted chunk timeline overrun. */
export function isAgentOutputSilent(): boolean {
  return !agentSpeaking && agentOutputVolume < AUDIBLE;
}

/**
 * Where the wait between two parameters actually goes. The silence before a step
 * is mostly the agent turn that produces its sentence, so seeing it separated
 * from the step's own duration is the only way to tune this without guessing.
 *
 *   localStorage.setItem("tsoles.voiceTiming", "1"); location.reload();
 */
const TIMING_STORAGE_KEY = "tsoles.voiceTiming";

function timingEnabled(): boolean {
  try {
    return window.localStorage.getItem(TIMING_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

let lastStepEndedAtForLog = 0;

export function emitParamStepStart(detail: {
  fieldKey: string;
  foot: number;
  stepIndex: number;
  startedAt: number;
  plannedMs: number;
  line: string;
}): void {
  if (timingEnabled()) {
    const silence = lastStepEndedAtForLog ? detail.startedAt - lastStepEndedAtForLog : 0;
    // eslint-disable-next-line no-console
    console.log(
      `[voice] START step ${detail.stepIndex} ${detail.fieldKey}`,
      `| silence before: ${silence}ms`,
      `| pace: ${VOICE_PACE}`,
      `| gap: ${VOICE_PARAM_GAP_MS}ms`,
      `| planned: ${detail.plannedMs}ms`
    );
  }
  document.dispatchEvent(new CustomEvent(VOICE_PARAM_STEP_START, { detail }));
}

export function emitParamStepEnd(detail: {
  fieldKey: string;
  foot: number;
  stepIndex: number;
  startedAt: number;
  endedAt: number;
  plannedMs: number;
  line: string;
}): void {
  if (timingEnabled()) {
    lastStepEndedAtForLog = detail.endedAt;
    // eslint-disable-next-line no-console
    console.log(
      `[voice] END   step ${detail.stepIndex} ${detail.fieldKey}`,
      `| actual: ${detail.endedAt - detail.startedAt}ms`,
      `| planned: ${detail.plannedMs}ms`,
      `| line: "${detail.line}"`
    );
  }
  document.dispatchEvent(new CustomEvent(VOICE_PARAM_STEP_END, { detail }));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export async function waitMs(ms: number): Promise<void> {
  if (ms <= 0) return;
  await sleep(ms);
}

/**
 * This session's measured pace, seeded from the constants and corrected by the
 * lines the assistant has actually spoken.
 *
 * A seed can only ever be close: pace depends on the voice, on the model and on
 * the language, and the tutorial runs in four of them. Being wrong about it is
 * not harmless, because everything downstream is derived from estimateSpeechMs
 * — how long a camera move is given, how much of a line must have played before
 * a silence is believed — so an estimate that runs long leaves the picture
 * holding still after the words have stopped.
 */
let wordsPerSec = VOICE_WORDS_PER_SEC;
let hanPerSec = VOICE_HAN_PER_SEC;

/** How much of a new measurement to take, so one odd line cannot swing the pace. */
const PACE_BLEND = 0.35;
/** Bounds a measurement has to fall inside to be believed at all. */
const PACE_MIN = 1.6;
const PACE_MAX = 5;

/**
 * Words a token is actually spoken as.
 *
 * Counting whitespace-separated tokens reads "350,000" as one word when the
 * voice says four, which made the estimate for the pressure line several
 * seconds short of the truth. Figures are weighted by their digits, and a
 * hyphen or a slash is a word boundary out loud even though it is not one on
 * the page ("voice-assisted", "1/6").
 */
function spokenWordsIn(token: string): number {
  const digits = (token.match(/\d/g) || []).length;
  if (!digits) return 1;
  return Math.max(1, Math.round(digits * 0.8));
}

/** What a line is counted in: characters for Chinese, words for everything else. */
function speechUnits(text: string): { han: boolean; count: number } {
  const han = (text.match(/[\u4e00-\u9fff]/g) || []).length;
  if (han >= 4) return { han: true, count: han };
  const tokens = text.trim().split(/[\s\-/]+/).filter(Boolean);
  return { han: false, count: tokens.reduce((sum, t) => sum + spokenWordsIn(t), 0) };
}

/** Rough spoken length of a line, used to size narration to a camera window. */
export function estimateSpeechMs(text: string): number {
  const { han, count } = speechUnits(text);
  if (!count) return 0;
  return Math.round((count / (han ? hanPerSec : wordsPerSec)) * 1000);
}

/**
 * Report what a line really took, so the next estimate is closer.
 *
 * Called with the span between the first audible frame and the silence that
 * followed it, which the chunk timeline knows exactly. Short lines are ignored:
 * there the lead-in and the tail are most of what was measured, so they say more
 * about the connection than about the voice.
 */
export function noteSpokenLine(text: string, spokenMs: number): void {
  if (!(spokenMs > 1200)) return;
  const { han, count } = speechUnits(text);
  if (count < (han ? 12 : 8)) return;
  const measured = count / (spokenMs / 1000);
  if (!Number.isFinite(measured) || measured < PACE_MIN || measured > PACE_MAX) return;
  if (han) hanPerSec = hanPerSec * (1 - PACE_BLEND) + measured * PACE_BLEND;
  else wordsPerSec = wordsPerSec * (1 - PACE_BLEND) + measured * PACE_BLEND;
  voiceLog("pace", Math.round((han ? hanPerSec : wordsPerSec) * 100) / 100);
}

/** The pace in use, for the timing log and for tests. */
export function spokenPace(): { wordsPerSec: number; hanPerSec: number } {
  return { wordsPerSec, hanPerSec };
}

/** Wait for the first audible TTS frame. Long timeout: slow networks must not desync.
 *  requireRisingEdge: must see quiet first, then speech — so leftover TTS from a
 *  previous turn is not mistaken for the line we just sent (Replay mid-speech).
 *  requireAudio: ignore the agent "speaking" mode flag — it can flip true before
 *  any sound, which made the tour skip the welcome and jump to pressure. */
export async function waitUntilAgentSpeaking(
  timeoutMs = VOICE_SPEAK_START_TIMEOUT_MS,
  opts?: { requireRisingEdge?: boolean; requireAudio?: boolean }
): Promise<"speaking" | "timeout"> {
  const started = Date.now();
  const isLive = () =>
    opts?.requireAudio ? isAgentAudible() : isAgentSpeaking() || isAgentAudible();
  let sawQuiet = !opts?.requireRisingEdge || !isLive();
  while (Date.now() - started < timeoutMs) {
    if (isLive()) {
      if (sawQuiet) return "speaking";
    } else {
      sawQuiet = true;
    }
    await sleep(40);
  }
  return "timeout";
}

/**
 * Wait for the output audio to fall quiet and stay quiet, which is how the
 * pause the assistant was cued to take is recognised from the client.
 */
export async function waitUntilAgentQuiet(
  timeoutMs = VOICE_STEP_PAUSE_WAIT_MS,
  quietMs = VOICE_PAUSE_QUIET_MS
): Promise<"quiet" | "timeout"> {
  const started = Date.now();
  let quietSince = 0;
  while (Date.now() - started < timeoutMs) {
    if (isAgentAudible()) {
      quietSince = 0;
    } else {
      if (!quietSince) quietSince = Date.now();
      if (Date.now() - quietSince >= quietMs) return "quiet";
    }
    await sleep(40);
  }
  return "timeout";
}

/** Wait for the first audible frame after a pause, which is the next sentence. */
export async function waitUntilAgentAudible(
  timeoutMs = VOICE_STEP_SPEAK_START_TIMEOUT_MS
): Promise<"speaking" | "timeout"> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (isAgentAudible()) return "speaking";
    await sleep(40);
  }
  return "timeout";
}

/** Poll until the queue has work, or give up. */
export async function waitForQueueItems(
  isEmpty: () => boolean,
  timeoutMs: number,
  pollMs = 80
): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (!isEmpty()) return true;
    await sleep(pollMs);
  }
  return !isEmpty();
}
